import Text from '@/components/ui/custom/dataDisplay/Text';
import Skeleton from '@/components/ui/custom/Skeleton';
import TooltipHandler from '@/components/ui/custom/TooltipHandler';
import useListService from '@/data/hook/useListService';
import { courseLinesQueryKeys } from '@/data/services/querykeys';
import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import { twMerge } from 'tailwind-merge';
import { RenderHistoryStatusIcon } from './HistoricPanel';
import { LINE_NAME_MAP } from '@/constants';

type HistoricCourseTagProps = {
  progress: SimplifiedCourseProgress[];
  lineName: string;
};

const lineConfig: Record<string, { text: string; className: string }> = {
  [LINE_NAME_MAP.ctrlKids]: { text: 'KIDS', className: 'bg-warning' },
  [LINE_NAME_MAP.ctrlTeens]: { text: 'TEENS', className: 'bg-primary' },
  [LINE_NAME_MAP.ctrlYoung]: { text: 'YOUNG', className: 'bg-secondary' },
};

export default function HistoricCourseTag({
  progress,
  lineName,
}: HistoricCourseTagProps) {
  const { text, className } = lineConfig[lineName] ?? {
    text: '',
    className: '',
  };

  if (!progress.length) {
    return null;
  }

  return (
    <div
      className={`flex gap-2 items-center text-base-100 w-fit rounded-full px-2.5 py-0.5 ${className}`}
    >
      <Text text={text} format="rubik-500" className="uppercase text-12" />
      <div className="flex gap-1 items-center">
        {progress
          .sort(
            (a, b) =>
              (a.coursePath.course.module?.order ?? 0) -
              (b.coursePath.course.module?.order ?? 0),
          )
          .map(progress => (
            <TooltipHandler
              key={progress.id}
              renderTooltip
              tooltipMessage={progress.coursePath.course.abbreviation}
            >
              <RenderHistoryStatusIcon
                iconClassName="text-base-100 w-4 h-4"
                status={progress.enrollmentStatus}
              />
            </TooltipHandler>
          ))}
      </div>
    </div>
  );
}

type HistoricTagGroupProps = {
  progress?: SimplifiedCourseProgress[];
  isLoading?: boolean;
  className?: string;
};

export const HistoricTagGroup = ({
  progress = [],
  isLoading,
  className,
}: HistoricTagGroupProps) => {
  const { results: lines, isLoading: isLoadingLines } = useListService(
    courseLinesQueryKeys.list({
      pageSize: 50,
    }),
  );
  if (isLoading || isLoadingLines)
    return <Skeleton className="rounded-full bg-primary-content h-3.5 w-20" />;

  if (progress.length) {
    return (
      <div className={twMerge('flex flex-row gap-2 w-fit', className)}>
        {lines.map(line => (
          <HistoricCourseTag
            key={line.id}
            progress={progress.filter(
              item =>
                item.coursePath.course.module?.courseLine?.name === line.name,
            )}
            lineName={line.name}
          />
        ))}
      </div>
    );
  }

  return null;
};
